import { Subscription } from 'rxjs';
import { ICompany } from '@collections/companies/interfaces/company.interface';
import { IUser } from '@collections/users/interfaces/user.interface';
import { IBreadcrumb } from '../breadcrumbs/breadcrumbs.service';
import { IProduct } from '@collections/products/interfaces/product.interface';
import { INews } from '@collections/news/interfaces/news.interface';
import { Inject, Injectable, RendererFactory2, ViewEncapsulation, Renderer2, LOCALE_ID, OnDestroy, Optional, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { HOST_URL } from '@shared/tokens/host-url';
import { META_TAG_ROBOTS_ENABLED } from '@shared/tokens/meta-tag-robots.enabled';
import { ellipsis } from '@shared/utilities/text';
import { LanguagesService } from '@shared/services/languages/languages.service';
import { IPathLocale } from '@collections/common/interfaces/path-locale.interface';
import { defaultMetadata, PageMetadata, RobotsIndexEnum, XDEFAULT_LANG } from './meta-data.commons';
import { BreadcrumbsService } from '../breadcrumbs/breadcrumbs.service';
import { RoutesService } from '../routes/route.service';
import { IAddress } from '@collections/common/interfaces';
import { IPortalEvent } from '@collections/portal-events/interfaces/portal-events.interface';
import { CloudinaryService, ResizeModeEnum } from '@shared/services/cloudinary/cloudinary.service';
import { Platform } from '@angular/cdk/platform';
import { AnalyticsService } from '@shared/services/google-tag-manager/analytics/analytics.service';

export const TITLE_SUFFIX = ' | expometals';
export const SITE_NAME = 'expometals.net';
@Injectable()
export class MetadataService implements OnDestroy {
  private renderer: Renderer2;
  private breadcrumbs: IBreadcrumb[] = [];
  private siteUrl: string;
  private bcsSubscription: Subscription;
  alternateLangs = signal<IPathLocale[]>([]);

  constructor(
    private metaTagService: Meta,
    private titleService: Title,
    @Inject(HOST_URL) private hostUrl: string,
    @Inject(META_TAG_ROBOTS_ENABLED) private metaTagRobotsEnabled: RobotsIndexEnum,
    @Inject(LOCALE_ID) private langCode: string,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private languagesService: LanguagesService,
    private breadcrumbsService: BreadcrumbsService,
    private routesService: RoutesService,
    private cloudinaryService: CloudinaryService,
    private platform: Platform,
    @Optional() private analyticsService: AnalyticsService
  ) {

    this.renderer = this.rendererFactory.createRenderer(this.document, {
      id: '-1',
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {}
    });

    this.siteUrl = `${this.hostUrl}/${this.langCode}`;
    this.bcsSubscription = this.breadcrumbsService.breadcrumbs$.subscribe(b => this.breadcrumbs = b);
  }

  public appendMetadata(
    metadata: Partial<PageMetadata>,
    canonicalUrl?: IPathLocale,
    alternateLangs?: IPathLocale[] | 'auto',
    index: RobotsIndexEnum = RobotsIndexEnum.index_follow,
    entity?: any,
    carousel?: string[]
  ): void {
    const pageMetadata: PageMetadata = { ...defaultMetadata, ...metadata };
    this.titleService.setTitle(pageMetadata.title + TITLE_SUFFIX);

    const currentLang = this.languagesService.getCurrentLangCode();

    pageMetadata.description = pageMetadata.description ? pageMetadata.description : '';
    const currentUrl = this.router.url;
    const metaDefinitions: MetaDefinition[] = this.generateMetaDefinitions(pageMetadata, this.routesService.getAbsoluteUrl(currentUrl));

    const tags = metaDefinitions;

    if (!canonicalUrl || (canonicalUrl.path === currentUrl && canonicalUrl.lang === currentLang)) {
      canonicalUrl = { lang: currentLang, path: currentUrl };

      // se abilitato questo token, consente di impostare il meta robots. (di default impostato su noindex-nofollow)
      if(this.metaTagRobotsEnabled) {
        tags.push({ name: 'robots', content: index });
      }

    }

    if (this.platform.isBrowser) {
      this.analyticsService.trackPageView(pageMetadata);
      for (let tag of tags) {
        this.metaTagService.updateTag(tag);
      }
    } else {
      this.metaTagService.addTags(tags);
    }  

    this.appendCanonicalURL(canonicalUrl);
    const currentAbsoluteUrl = this.routesService.getAbsoluteUrl(currentUrl, this.langCode);
    this.appendStructuredData(currentAbsoluteUrl, metadata, entity, carousel);

    // per le alternative lang utilizzo il setup che include tutte le url dei linguaggi disponibili,
    // (compreso il self-referencing url) e un x-default aggiuntivo con lo stesso url dell'inglese.
    // Non credo faccia differenza rispetto ad omettere l'hreflang per l'inglese, ma nel dubbio...

    // However it is also possible (and valid) to define a page for a specific locale/language AND also as the x-default hreflang -
    // in this case it would both serve as the correct page for the targeted location,
    // but also as the fallback when no other pages are suitable.
    // https://sitebulb.com/hints/international/hreflang-annotation-also-x-default/
    if (canonicalUrl.path === currentUrl) {
      // se url canonico punta ad un diverso url ometto gli hreflang
      // If you are using a canonical tag solution on your website,
      // make sure that URLs that have a canonical tag pointing to another URL do not receive hreflang annotations.
      // hreflang annotations are okay for URLs that point to themselves via canonical tag and for URLs that do not have canonical tags.
      // https://www.searchviu.com/en/hreflang-canonical/#:~:text=If%20you%20are%20using%20a,do%20not%20have%20canonical%20tags.

      if (currentUrl.indexOf('?') > 0) { // esclude hreflang for urls with parameteres
        return;
      }
      let hrefLangs: IPathLocale[] = [];
      if (this.platform.isBrowser) {
        this.document.querySelectorAll(`link[rel="alternate"]`).forEach( link => {
          this.document.head.removeChild(link); 
        }); 
      }
      if (alternateLangs === 'auto') {
        hrefLangs = this.generateHrefLangs();
      } else {
        hrefLangs = alternateLangs || [];
      }
      this.alternateLangs.set(hrefLangs);
      for (const hrefLang of hrefLangs) {
        this.appendHrefLang(hrefLang);
      }

      if (hrefLangs.length > 0) {
        let xDefault: IPathLocale = hrefLangs.find(al => al.lang === XDEFAULT_LANG);
        if (xDefault) {
          this.appendHrefLang(xDefault, true);
        } else {
          // per quelli che non hanno l'inglese, andrebbe aggiunta una lingua di default
          // in mancanza, prendo la prima utile prediligendo l'italiano.
          xDefault = hrefLangs.find(al => al.lang === 'it') || hrefLangs[0];
          this.appendHrefLang(xDefault, true);
        }
      }
    }

  }

  generateHrefLangs(): IPathLocale[] {
    return this.languagesService.getLanguages().map(l => {
      return { lang: l.code, path: this.router.url };
    });
  }

  private generateMetaDefinitions(metadata: PageMetadata, url: string): MetaDefinition[] {
    // mancano properties: fb:app_id, fb:admins, twitter:app:id:iphone, twitter:app:id:ipad
    const metas = [
      { name: 'title', content: metadata.title + TITLE_SUFFIX },
      { property: 'og:title', content: metadata.newsTitle ? metadata.newsTitle + TITLE_SUFFIX : metadata.title + TITLE_SUFFIX },
      { name: 'description', content: metadata.description },
      { property: 'og:description', content: metadata.description },
      { property: 'og:url', content: url },
      { property: 'og:type', content: metadata.pageType },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'twitter:site', content: '@Expometals' },
      { property: 'twitter:title', content: metadata.newsTitle ? metadata.newsTitle + TITLE_SUFFIX : metadata.title + TITLE_SUFFIX},
      { property: 'twitter:url', content: url },
      { property: 'twitter:domain', content: 'Expometals.net' },
      { property: 'twitter:creator', content: '@Expometals' },
    ];

    if (metadata.images) {
      for (const image of metadata.images) {
        metas.push({ property: 'twitter:card', content: 'summary_large_image' })
        metas.push({ property: 'twitter:image', content: this.getSocialImageUrl(image) });
        metas.push({ property: 'og:image', content: this.getSocialImageUrl(image) });
        metas.push({property: 'og:image:width', content: '1200'});
        metas.push({property: 'og:image:height', content: '672'});
      }
    }

    if (metadata.pageType === 'news') {
      metas.push(
        { property: 'article:publisher', content: 'https://www.facebook.com/expometals.net/' },
        { property: 'article:published_time', content: metadata.publishDate }
      );
      if (metadata.authorUrl) {
        metas.push({ property: 'article:author', content: metadata.authorUrl });
      }
    } else if (this.platform.isBrowser) { // avoid duplicates on browser side
      for (let selector of ['article:publisher', 'article:published_time', 'article:author']) {
        this.metaTagService.removeTag(`property="${selector}"`);  
      } 
    }

    return metas;
  }

  // https://moz.com/blog/writing-structured-data-guide
  // TODO:
  // Verificare dati e elogo di Organization Expometals
  // il dato Author delle news deve contenere l'url al profilo /team/user-name se è un admin, o alla pagina del profilo se un  cliente. Da valutare.
  // Impostare @type per gli Eventi portale
  // Valutare padiglioni / Categorie ecc.
  private appendStructuredData(currentAbsoluteUrl: string, {title, description, images, company}: Partial<PageMetadata>, entity?: any, carousel?: string[]): void {
    const siteUrl = this.siteUrl;
    const data = {
      '@context': 'http://schema.org',
      '@graph': []
    };

    if (!company) {
      data['@graph'].push(this.generateExpWebSiteSD(siteUrl));
    }

    data['@graph'].push(this.generateExpWebPageSD(siteUrl, currentAbsoluteUrl, title, description, images));

    if (this.breadcrumbs?.length > 0) {
      data['@graph'].push(this.generateBreadcrumbSD(currentAbsoluteUrl));
    }

    if (entity) {
      let entityData;
      switch (entity.__typename) {
        case 'Company':
            this.generateOrganizationSD(company, currentAbsoluteUrl);
          break;
        case 'News':
          entityData = this.generateNewsSD(entity, currentAbsoluteUrl, company);
          break;
        case 'Product':
          entityData = this.generateProductSD(entity, currentAbsoluteUrl, company);
          break;
        case 'PortalEvent':
          entityData = this.generatePortalEventSD(entity, currentAbsoluteUrl);
          break;
      }

      if (entityData) {
        data['@graph'].push(entityData);
      }

      if (carousel) {
        data['@graph'].push(this.generateCarouselSD(carousel, currentAbsoluteUrl));
      }
    }
    this.renderJsonLd(data);
  }

  private generateExpWebSiteSD(siteUrl: string): any {
    return {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: SITE_NAME,
      url: siteUrl,
      sameAs: [
        'https://www.facebook.com/expometals.net',
        'https://www.linkedin.com/company/expometals-net',
        'https://twitter.com/expometals',
        'https://www.youtube.com/channel/UCYhDx5mckKj-FUrhtgPEx6g'
      ],
      logo: {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/#logo`,
        inLanguage: this.langCode,
        url:  'https://res.cloudinary.com/expometals/image/upload/v1649826786/assets/exp-logo.png',
        contentUrl:  'https://res.cloudinary.com/expometals/image/upload/v1649826786/assets/exp-logo.png',
        width: 500,
        height: 500,
        caption: SITE_NAME
      },
      image: {
        '@id': `${siteUrl}/#logo`,
      },
      // founder: {
      //   '@type': 'Person',
      //   name: 'Dell\'Oro Davide',
      //   url: 'http://localhost:4200/team/davide-delloro',
      //   sameAs: 'https://www.linkedin.com/in/davide-dell-oro-a0757629/'
      // },
      // foundingDate: '2010-05-01',
      // numberOfEmployees: 5,
      slogan: $localize`The International Online Fair`,
      description: $localize`expometals.net is the international digital marketing platform for the metalworking field, namely wire, springs, fasteners, tube, sheet metal, and related industries. From raw material to finished products, ferrous and non-ferrous metals.`,
      // legalName: 'Dell\'Oro di Dell\'Oro Davide'
    };
  }

  private generateExpWebPageSD(siteUrl: string, currentAbsoluteUrl: string, title: string, description: string, images: string[]): any {
    const obj = {
      '@type': 'WebPage',
      '@id': `${currentAbsoluteUrl}/#webpage`,
      url: currentAbsoluteUrl,
      name: title,
      description: description,
      inLanguage: this.langCode,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      breadcrumb: {
        '@id': `${currentAbsoluteUrl}/#breadcrumblist`,
      }
    };
    if(images.length > 0 && images[0]) {
      obj['image'] =  {
        '@type': 'ImageObject',
        '@id': `${currentAbsoluteUrl}/#primaryimage`,
        url: this.getSocialImageUrl(images[0]),
      };
      obj['primaryImageOfPage'] = {
        '@id': `${currentAbsoluteUrl}/#primaryimage`,
      };
    }
    return obj;
  }
  private generateBreadcrumbSD(currentAbsoluteUrl: string): any {
    const items = [];
    for (const [i, breadcrumb] of this.breadcrumbs.entries()) {
      const listItem = {
        '@type': 'ListItem',
        'position': i + 1,
        name: breadcrumb.label,
      };
      // Aggiungi l'url solo se non è l'ultimo elemento
      if (i < this.breadcrumbs.length - 1) {
        listItem['item'] = this.routesService.getAbsoluteUrl(breadcrumb.url, this.langCode);
      }
      items.push(listItem);
    }
    return {
      '@type': 'BreadcrumbList',
      '@id': `${currentAbsoluteUrl}/#breadcrumblist`,
      itemListElement: items
    };
  }

  private generateOrganizationSD(company: ICompany, currentAbsoluteUrl: string): any {
    const companyUrl = this.routesService.getCompanyUrl(company, null, true);
    return {
      '@type': 'Organization',
      name: company.fullName,
      url: companyUrl,
      sameAs: [
        company.webSite,
        ...(company.socials?.map(s => s.url) || [])
      ],
      logo: {
        '@type': 'ImageObject',
        '@id': `${companyUrl}/#logo`,
        inLanguage: this.langCode,
        url:  this.getSmallPictureUrl(company.logo),
        width: 150,
        height: 150,
        caption: company.fullName
      },
      address: this.generateAddressSD(company.addresses[0]),
      image: {
        '@id': `${companyUrl}/#logo`,
      }
    };
  }

  private generateAuthorSD(user: IUser, currentAbsoluteUrl: string): any {
    return {
      '@type': 'Person',
      '@id': `${currentAbsoluteUrl}#author`,
      name: user.profile.fullName,
      image: this.getSmallPictureUrl(user.profile.picture),
      url: this.routesService.getUserUrl(user, true)
    };
  }

  private generateAddressSD(address: IAddress): any {
    return {
      '@type': 'PostalAddress',
      streetAddress: address.street,
      addressLocality: address.city,
      postalCode: address.postalCode,
      addressRegion: address.regionCode,
      addressCountry: address.countryCode
    };
  }

  generateNewsSD(news: INews, currentAbsoluteUrl: string, company?: ICompany): any {
    // const companyUrl = this.getAbsoluteUrl(this.routesService.getCompanyUrl(company));
    return {
      '@type': [
        'Article',
        'NewsArticle'
      ],
      isPartOf: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      },
      author: this.generateAuthorSD(news.author, currentAbsoluteUrl),
      publisher: company ?
      this.generateOrganizationSD(company, currentAbsoluteUrl) :
      {
        '@id': `${this.siteUrl}#organization`
      },
      mainEntityOfPage: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      },
      headline: news.title,
      image: {
        '@id': `${currentAbsoluteUrl}/#primaryimage`
      },
      datePublished: news.publishDate,
      dateModified: news.updatedAt ? news.updatedAt : news.publishDate,
      ...(news.tags && {keywords: news.tags && news.tags.length > 0 ? news.tags.reduce(
        (accumulator, currentValue) => accumulator ? accumulator + ', ' + currentValue.tag : currentValue.tag,
        '',
      ) : ''})/* ,
      ...(news.categories && {category: news.categories && news.categories.length > 0 ? news.categories[0].name : '' }) dato non presente nello schema di News Article di schema.org*/
    };
  }

  generateProductSD(product: IProduct, currentAbsoluteUrl: string, company?: ICompany): any {
    return {
      '@type': 'Product',
      // isPartOf: {
      //   '@id': `${currentAbsoluteUrl}/#webpage`
      // },
      name: product.name,
      description: product.shortDescription,
      image: this.getSocialImageUrl(product.images[0]),
      brand: this.generateOrganizationSD(company, currentAbsoluteUrl),
      aggregateRating: {
        '@type': "AggregateRating",
        ratingValue: (product.rating ? product.rating: 3).toString(),
        ratingCount: (product.ratingCount ? product.ratingCount: 1).toString()
      },
      mainEntityOfPage: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      },
      category: product.categories && product.categories.length > 0 ? this.URLtoText(product.categories[0].path) : ''
    };
  }

  generatePortalEventSD(portalEvent: IPortalEvent, currentAbsoluteUrl: string): any {
    const eventData: any = {
      '@type': 'Event',
/*       isPartOf: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      }, */
      name: portalEvent.title,
      description: portalEvent.summary,
      startDate: portalEvent.startDate,
      endDate: portalEvent.endDate,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: portalEvent.eventType == "webinar" ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode",
      ...(portalEvent.address && {
        location: {
          '@type': 'Place',
          address: this.generateAddressSD(portalEvent.address),
        }
      }),
      mainEntityOfPage: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      },
      image: {
        '@id': `${currentAbsoluteUrl}/#primaryimage`
      },
    };

    if (portalEvent.locationName) {
      eventData.location.name = portalEvent.locationName
    }

    if (portalEvent.organizer) {
      eventData.organizer = {
        '@type': 'Organization',
        name: portalEvent.organizer
      };
    }

    if (portalEvent.performer) {
      eventData.performer = {
        '@type': 'Person',
        name: portalEvent.performer
      };
    }

    if (portalEvent.offerUrl) {
      eventData.offers = {
        '@type': 'Offer',
        url: portalEvent.offerUrl,
        price: portalEvent.offerPrice,
        priceCurrency: 'EUR'
      };
      if(new Date() < new Date(portalEvent.startDate)){
        eventData.offers.availability = "https://schema.org/InStock"
      }
      else{
        eventData.offers.availability = "https://schema.org/SoldOut"
      }
    }

    return eventData;
  }

  generateCarouselSD(items: string[], currentAbsoluteUrl: string): any {
    return {
      '@type': 'ItemList',
      itemListElement: [
        ...items.map((url, index) => Object.assign({}, {
          '@type': 'ListItem',
          position: (index + 1),
          url: `${this.siteUrl}${url}`
        }))
      ],
      isPartOf: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      },
      mainEntityOfPage: {
        '@id': `${currentAbsoluteUrl}/#webpage`
      },
    };
  }

  private renderJsonLd(data: any): void {
    try {
      let script = null;
      if (this.platform.isBrowser) {
        script = this.document.getElementById('structuredDataScript');
      }
      
      if (!script) {
        script = this.renderer.createElement('script');
        this.renderer.setAttribute(script, 'id', 'structuredDataScript');
        this.renderer.setAttribute(script, 'type', 'application/ld+json');
        this.renderer.appendChild(this.document.head, script);
      }

      // console.log(data);
      const scriptText: string = JSON.stringify(data);
      this.renderer.setProperty(script, 'innerHTML', scriptText);
      
    } catch (e) {
      console.error('Error on rendering JsonLD data : ', e);
    }
  }

  private appendCanonicalURL(url: IPathLocale): void {
    try {
      const absoluteUrl = this.routesService.getAbsoluteUrl(url.path, url.lang);
      let link = null;
      if (this.platform.isBrowser) {
        link = this.document.querySelector('link[rel="canonical"]');
      }
      if (!link) {
        link = this.renderer.createElement('link');
        this.renderer.setAttribute(link, 'rel', 'canonical');
        this.renderer.appendChild(this.document.head, link);
      }

      this.renderer.setAttribute(link, 'href', absoluteUrl);

    } catch (e) {
      console.error('Error within linkService : ', e);
    }
  }

  private appendHrefLang(pathLocale: IPathLocale, xDefault: boolean = false): void {
    try {
      if (!pathLocale.path) {
        return;
      }
      const absoluteUrl = this.routesService.getAbsoluteUrl(pathLocale.path, pathLocale.lang);
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'alternate');
      this.renderer.setAttribute(link, 'hreflang', xDefault ? 'x-default' : pathLocale.lang);
      this.renderer.setAttribute(link, 'href', absoluteUrl);
      this.renderer.appendChild(this.document.head, link);
    } catch (e) {
      console.error('Error within linkService : ', e);
    }
  }

  private getSocialImageUrl(imageUrl: string): string {
    return this.cloudinaryService.getImageUrl(imageUrl,1200, 672, ResizeModeEnum.fill, false);
  }

  private getSmallPictureUrl(image: string): string {
    return this.cloudinaryService.getImageUrl(image, 150, 150, ResizeModeEnum.scale, false);
  }

  appendRSSLink(isGeneralNews:boolean, lang: string): void {
    try{
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'alternate')
      this.renderer.setAttribute(link, 'type', 'application/rss+xml')
      if(isGeneralNews){
        this.renderer.setAttribute(link, 'title', $localize`Expometals.net Market News`)
        this.renderer.setAttribute(link, 'href', `${this.hostUrl}/rss/${lang}/news`)
      }
      else{
        this.renderer.setAttribute(link, 'title', $localize`Expometals.net Company News`)
        this.renderer.setAttribute(link, 'href', `${this.hostUrl}/rss/${lang}/exhibitors-news`)
      }
      this.renderer.appendChild(this.document.head, link);
    }
    catch (e) {
      console.error('Error within rss linkService : ', e);
    }
  }

  private URLtoText(url: string): string {
    const segments = url.split('/').filter(Boolean); // rimuove stringhe vuote
    const lastTwo = segments.slice(-2); // prende gli ultimi 2 segmenti
    const formatted = lastTwo.map(s => 
      s.replace(/-/g, ' ').replace(/^./, c => c.toUpperCase()) // capitalizza
    );
    return formatted.join(' > ');
  }

  ngOnDestroy(): void {
    this.bcsSubscription.unsubscribe();
  }
}
